// 프로젝트 공용 axios(인터셉터 등록 모듈)를 거쳐 Authorization 헤더·401/403 처리가 그대로 붙는다 — utils/imageUpload.ts 와 같은 방식.
// 다른 세션이 axios.ts 를 고치는 중이라 대화방 호출은 이 파일에 따로 둔다(docs/application-thread-design.md "충돌 회피").
import axios, { AxiosResponse } from 'axios';
import '@/api/repository/axios';
import type {
    AttendanceResult, HireOptions, InterviewPayload, InterviewResult, OfferPayload, RecruitmentPromptAnswer, ThreadListPage, ThreadResponse, ThreadSummary, ThreadView,
} from './threadTypes';

/** 화면 → 서버 경로(앞의 /api 는 프록시가 뗀다 — 기존 `/api/applications/{id}/status` 와 같다) */
const base = (applicationId: number | string): string => `/api/applications/${applicationId}/thread`;

/** 성공 code 가 아닌 응답 — 서버가 200 + 경고(상태는 바뀜)를 줄 때도 여기로 온다 */
export class ThreadResponseError extends Error {
    readonly code: number;

    constructor(code: number, message: string) {
        super(message);
        this.code = code;
    }
}

/** 서버 SuccessResponse 의 code 가 0 이 아니면 그 문구로 던진다 */
const unwrap = <T>(response: AxiosResponse<ThreadResponse<T>>): T => {
    const body: ThreadResponse<T> = response.data;
    if (!body || typeof body !== 'object') throw new ThreadResponseError(-1, '');
    if (body.code !== 0) throw new ThreadResponseError(body.code, body.message ?? '');
    return body.result;
};

/** 동작 응답 — 서버가 갱신된 ThreadView 를 주면 그것을, 아니면 null(화면이 다시 불러온다) */
const viewOrNull = (result: unknown): ThreadView | null => {
    if (result && typeof result === 'object' && Array.isArray((result as ThreadView).events) && Array.isArray((result as ThreadView).actions)) {
        return result as ThreadView;
    }
    return null;
};

const post = async (applicationId: number | string, path: string, body?: object): Promise<ThreadView | null> => {
    const response = await axios.post<ThreadResponse<unknown>>(`${base(applicationId)}/${path}`, body ?? {});
    return viewOrNull(unwrap(response));
};

/** 안 읽은 수 한 번에 물어볼 수 있는 지원 건 수(서버와 같은 값) */
export const UNREAD_IDS_MAX: number = 50;

export const threadApi = {
    /** 내 대화방 전체의 안 읽은 수 합(개수 제한 없음) — 실패하면 0 */
    unreadTotal: async (): Promise<number> => {
        try {
            const response = await axios.get<ThreadResponse<{ total: number }>>('/api/applications/threads/unread-total', { skipAuthHandler: true });
            return unwrap(response)?.total ?? 0;
        } catch {
            return 0;
        }
    },
    /** 내 대화방 목록(최근 활동순) */
    list: async (page: number, size: number): Promise<ThreadListPage> => {
        const response = await axios.get<ThreadResponse<ThreadListPage>>('/api/applications/threads', { params: { page, size } });
        return unwrap(response);
    },
    /**
     * 여러 지원 건의 안 읽은 수 — { 지원서ID: 수 }. 50개씩 나눠 묻는다. 실패하면 빈 값(배지만 안 보임).
     * 당사자가 아닌 건·없는 건은 빠져 온다(0 으로 본다).
     */
    unreadCounts: async (applicationIds: Array<number | string>): Promise<Record<string, number>> => {
        const ids: string[] = Array.from(new Set(applicationIds.map(String).filter((id) => /^\d+$/.test(id))));
        if (ids.length === 0) return {};
        const chunks: string[][] = [];
        for (let i = 0; i < ids.length; i += UNREAD_IDS_MAX) chunks.push(ids.slice(i, i + UNREAD_IDS_MAX));
        try {
            const results = await Promise.all(chunks.map((chunk) =>
                axios.get<ThreadResponse<Record<string, number>>>('/api/applications/threads/unread', { params: { ids: chunk.join(',') }, skipAuthHandler: true })
                    .then(unwrap)));
            return Object.assign({}, ...results);
        } catch {
            return {};
        }
    },
    /**
     * 대화방 보기. 401 은 공용 처리(로그인 화면으로 보냄)를 거치지 않고 화면이 "볼 수 없는 대화방"으로 보여 준다 —
     * 로그인한 사람이 남의 지원 건 주소로 들어와도 401(서버 IDOR 처리)이라, 로그인 화면으로 보내면 엉뚱하다.
     */
    get: async (applicationId: number | string): Promise<ThreadView> => {
        const response = await axios.get<ThreadResponse<ThreadView>>(base(applicationId), { skipAuthHandler: true });
        return unwrap(response);
    },
    /** 합격 시트 선택지(공고 종류·근무 날짜·기간 요약·면접 기본 장소) — 읽음 처리 없음 */
    hireOptions: async (applicationId: number | string): Promise<HireOptions> => {
        const response = await axios.get<ThreadResponse<HireOptions>>(`${base(applicationId)}/hire-options`);
        return unwrap(response);
    },
    offer: (applicationId: number | string, payload: OfferPayload): Promise<ThreadView | null> => post(applicationId, 'offer', payload),
    interview: (applicationId: number | string, payload: InterviewPayload): Promise<ThreadView | null> => post(applicationId, 'interview', payload),
    interviewAccept: (applicationId: number | string): Promise<ThreadView | null> => post(applicationId, 'interview/accept'),
    interviewReschedule: (applicationId: number | string, memo: string): Promise<ThreadView | null> => post(applicationId, 'interview/reschedule', { memo }),
    interviewDecline: (applicationId: number | string, memo?: string): Promise<ThreadView | null> => post(applicationId, 'interview/decline', memo ? { memo } : {}),
    interviewResult: (applicationId: number | string, body: { result: InterviewResult; startAt?: string; memo?: string }): Promise<ThreadView | null> =>
        post(applicationId, 'interview/result', body),
    /** 여러 지원 건의 대화방 요약 — 50개씩, 실패하면 빈 값 */
    summaries: async (applicationIds: Array<number | string>): Promise<Record<string, ThreadSummary>> => {
        const ids: string[] = Array.from(new Set(applicationIds.map(String).filter((id) => /^\d+$/.test(id))));
        if (ids.length === 0) return {};
        const chunks: string[][] = [];
        for (let i = 0; i < ids.length; i += UNREAD_IDS_MAX) chunks.push(ids.slice(i, i + UNREAD_IDS_MAX));
        try {
            const results = await Promise.all(chunks.map((chunk) =>
                axios.get<ThreadResponse<Record<string, ThreadSummary>>>('/api/applications/threads/summary', { params: { ids: chunk.join(',') }, skipAuthHandler: true })
                    .then(unwrap)));
            return Object.assign({}, ...results);
        } catch {
            return {};
        }
    },
    drop: (applicationId: number | string, memo?: string): Promise<ThreadView | null> => post(applicationId, 'drop', memo ? { memo } : {}),
    /** 근무 확정·출근 확인 — [offerRef] = 화면이 본 근무 조건(offer.ref). 그새 일정이 바뀌었으면 409 "일정이 바뀌었어요" */
    confirm: (applicationId: number | string, offerRef?: string | null): Promise<ThreadView | null> => post(applicationId, 'confirm', offerRef ? { offerRef } : {}),
    decline: (applicationId: number | string, memo?: string): Promise<ThreadView | null> => post(applicationId, 'decline', memo ? { memo } : {}),
    attendance: (applicationId: number | string, result: AttendanceResult): Promise<ThreadView | null> => post(applicationId, 'attendance', { result }),
    /** 치과 — 최종 합격 뒤 출근일 보내기(startAt = 시간대 포함 ISO) */
    sendStart: (applicationId: number | string, body: { startAt: string; memo?: string }): Promise<ThreadView | null> => post(applicationId, 'start', body),
    /** 치과 — 일정 바꾸기. 출근(START)은 startAt, 근무는 근무 확정 요청과 같은 칸 */
    changeOffer: (applicationId: number | string, body: OfferPayload & { startAt?: string }): Promise<ThreadView | null> => post(applicationId, 'offer/change', body),
    /** 지원자 — 다른 날짜 요청(가능한 날짜 필수) */
    offerReschedule: (applicationId: number | string, memo: string, offerRef?: string | null): Promise<ThreadView | null> =>
        post(applicationId, 'offer/reschedule', offerRef ? { memo, offerRef } : { memo }),
    /** 지원자 — 입사 포기·근무 취소(사유 선택) */
    withdraw: (applicationId: number | string, memo?: string, offerRef?: string | null): Promise<ThreadView | null> =>
        post(applicationId, 'withdraw', { ...(memo ? { memo } : {}), ...(offerRef ? { offerRef } : {}) }),
    /** 치과 — 마감·다시 열기 질문 카드 답 */
    recruitmentPrompt: (applicationId: number | string, eventId: string, answer: RecruitmentPromptAnswer): Promise<ThreadView | null> =>
        post(applicationId, 'recruitment-prompt', { eventId, answer }),
    dispute: (applicationId: number | string, text: string): Promise<ThreadView | null> => post(applicationId, 'dispute', { text }),
    /** 신고 — eventId 가 있으면 그 메시지, 없으면 대화방 상대 */
    report: async (applicationId: number | string, body: { reason: string; detail?: string; eventId?: string }): Promise<void> => {
        const response = await axios.post<ThreadResponse<unknown>>(`${base(applicationId)}/report`, body);
        unwrap(response);
    },
    block: (applicationId: number | string): Promise<ThreadView | null> => post(applicationId, 'block'),
    unblock: async (applicationId: number | string): Promise<ThreadView | null> => {
        const response = await axios.delete<ThreadResponse<unknown>>(`${base(applicationId)}/block`);
        return viewOrNull(unwrap(response));
    },
    message: (applicationId: number | string, text: string): Promise<ThreadView | null> => post(applicationId, 'messages', { text }),
};

/** HTTP 상태 코드(없으면 null) */
export const httpStatusOf = (error: unknown): number | null => {
    const status: unknown = (error as { response?: { status?: unknown } } | null)?.response?.status;
    return typeof status === 'number' ? status : null;
};
